import React from 'react';
import { createStyles, makeStyles } from '@material-ui/styles';
import FilesClient from 'src/clients/FilesClient';
import {
  ENTITIES,
  FOLDER_FILE_KEY,
  NOTIFICATION_TIMELINE_DRAWER_WIDTH,
  User,
} from 'src/constants';
import { InboxPageContext } from 'src/context/inboxPageContext';
import {
  getExtensionFromFileKey,
  getFileNameFromFileKey,
} from 'src/components/Files/helpers';
import { InboxNotificationTable } from 'src/components/Inbox/InboxNotificationTable';
import { InboxEmptyState } from 'src/components/Inbox/InboxEmptyState';
import { ChannelContentToolbar } from '../Channels/ChannelContentToolbar';
import { FlagsContext } from 'src/context';
import history from 'src/history';
import {
  useDeleteNotificationMutation,
  useGetInboxNotificationDetailsQuery,
} from 'src/services/api/inboxApi';
import { InboxNotificationDetailItem } from 'src/entities/Notifications';
import { NotificationGroups } from 'src/store/notifications/types';
import { FormResponseDetail } from '../FormsV2/FormResponseDetail';
import { FormNotificationDetailsBreadcrumbs } from './FormNotificationContentBreadcrumbs';
import Button from '../Button';
import { ExportIcon, TrashIcon } from '../Icons';
import { red } from 'src/theme/colors';
import { useExportFormResponse } from 'src/hooks/useExportFormResponse';
import { RightSidebarShadow } from 'src/theme/shadows';
import { RightSidebarToggle } from '../RightSidebar/RightSidebarToggle';
import { RightSidebar } from '../RightSidebar';
import { NotificationTimeline } from './NotificationTimeline';
import { Theme } from '@material-ui/core';

export interface NotificationTableRowData extends User {
  rowId: string;
  name: string;
  location: string;
  channelId: string;
  userName: string;
  isDeleted?: boolean;
  fileExtension?: string;
  // these fields are used for file preview
  fileIdentityId?: string;
  fileKey?: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
      padding: 0,
    },
    deleteNotificationBtn: {
      color: red,
    },
    notificationTimelineDrawer: {
      padding: theme.spacing(3, 0),
      width: NOTIFICATION_TIMELINE_DRAWER_WIDTH,
      boxShadow: RightSidebarShadow,
      marginTop: 60,
    },
  }),
);

export const DELETED_FILE_NOTIFICATION_STATUS = '0_CANCELLED';

export const InboxContent = () => {
  const { EnableNotificationSidebarItemRedesign } =
    React.useContext(FlagsContext);
  const classes = useStyles();
  const { selectedNotification, setSelectedNotification } =
    React.useContext(InboxPageContext);
  const isFileUploadNotification =
    selectedNotification &&
    selectedNotification.notificationGroup === ENTITIES.NOTIFY_FILE_UPLOADED;

  const [notificationTableRowData, setNotificationTableRowData] =
    React.useState<NotificationTableRowData[]>([]);

  const {
    data: selectedNotificationDetails,
    isLoading: fileNotificationDetailsLoading,
  } = useGetInboxNotificationDetailsQuery(
    {
      id: encodeURIComponent(selectedNotification?.id || ''),
    },
    {
      skip: !selectedNotification?.id,
    },
  );

  const [deleteNotification] = useDeleteNotificationMutation();

  const [
    filesNotificationDetailsDataReady,
    setFilesNotificationDetailsDataReady,
  ] = React.useState(false);

  /**
   * This function will setup notification table data
   * based on selected notification.
   */
  const setupNotificationTableData = async () => {
    setFilesNotificationDetailsDataReady(true);
    // clearing last selected notification data
    setNotificationTableRowData([]);

    // don't do anything if no notification is selected
    if (!selectedNotification || !selectedNotification.channelId) return;

    const allChannelFiles = await FilesClient.GetFilesForChannelId(
      selectedNotification.channelId,
    );

    const rowData: NotificationTableRowData[] = [];
    const deletedFilesRowData = [];
    // get id's of file related to notification
    let notificationFileIds: string[] = [];

    // for file upload notifications
    if (isFileUploadNotification) {
      if (selectedNotificationDetails) {
        selectedNotificationDetails.items.forEach(
          (n: InboxNotificationDetailItem) => {
            const fileId = n.resourceId;
            if (fileId === 'default') return; // notification group itself has the 'default' id and we can ignore that

            // checking if the notification file is deleted or not
            if (n.isDeleted) {
              // to indicated a deleted file add a row in notification table
              // with name 'File was deleted'
              deletedFilesRowData.push({
                rowId: fileId,
                name: 'File was deleted',
                location: '',
                isDeleted: true,
              });
              return;
            }

            notificationFileIds.push(fileId);
          },
        );
      }
    } else {
      // for notifications which are not grouped
      // we know there will be only 1 file in those notifications
      const fileId = selectedNotification.data.split('/').at(2);
      // check if the file is deleted
      if (
        selectedNotification.data.includes(DELETED_FILE_NOTIFICATION_STATUS)
      ) {
        deletedFilesRowData.push({
          rowId: fileId,
          name: 'File was deleted',
          location: '',
          isDeleted: true,
        });
      } else if (fileId) {
        notificationFileIds = [fileId];
      }
    }

    const notificationFiles = allChannelFiles.filter((n) =>
      notificationFileIds?.includes(n.id),
    );

    notificationFiles.forEach((file) => {
      const { fields, owner } = file;

      // avoiding folders
      if (fields.fileKey === FOLDER_FILE_KEY) return;
      if (!owner) return;

      let fileExtension = '';

      // generating file name
      let name = getFileNameFromFileKey(fields.fileKey);
      // except for file links
      // add file extension in file name
      if (!fields.fileUrl) {
        fileExtension = getExtensionFromFileKey(fields.fileKey); // file extension
        name = `
           ${name}${fileExtension}`;
      }

      if (fields.fileUrl) {
        fileExtension = 'link';
      }

      // for file upload notification
      // we will show file owner info in the "From" column
      // and for Esign complete notification we will show
      // notification sender user info in "Signed by" column
      const userInfo =
        selectedNotification.notificationGroup === ENTITIES.NOTIFY_FILE_UPLOADED
          ? {
              userName: `${owner?.fields.givenName} ${owner?.fields.familyName}`,
              ...owner,
            }
          : {
              userName: selectedNotification.avatarInfo?.name,
              fields: {
                picture: selectedNotification.avatarInfo?.imageUrl,
                ...selectedNotification.avatarInfo,
              },
            };

      // get file size from notification item data
      const fileData = selectedNotificationDetails?.items.find(
        (fileNotification) =>
          fileNotification.resourceId === file.id &&
          !fileNotification.isDeleted,
      );
      const fileSize = fileData?.fileSize || 0;
      const fileIdentityId = file?.identityId || '';
      const item = {
        rowId: file.id,
        fileKey: file.fields.fileKey,
        fileIdentityId,
        name,
        fileExtension: fileExtension.replace('.', '').toLowerCase(), // removing "." from extension names e.g ".pdf" > "pdf"
        location: file.fields.path || '',
        channelId: selectedNotification.channelId,
        email: userInfo.fields.email,
        fallbackColor: userInfo.fields.fallbackColor,
        picture: userInfo.fields.picture,
        size: fileSize,
        ...userInfo,
      };

      rowData.push(item as NotificationTableRowData);
    });

    setNotificationTableRowData([
      ...rowData,
      ...deletedFilesRowData,
    ] as Array<NotificationTableRowData>);
    setFilesNotificationDetailsDataReady(false);
  };

  /**
   * This effect will setup notification table data
   * it listens to the selected notification details
   * fetched from the API and then setup the table data
   * based on that.
   */
  React.useEffect(() => {
    setupNotificationTableData();
  }, [selectedNotificationDetails, selectedNotification]);

  const notificationDetailsContentRenderer = () => {
    if (!selectedNotification) return null;

    // TODO: we still need to keep using notificationGroup
    // to check the notification type for now (for files and esignature). This will be
    // replaced by checking notificationEventType.
    if (
      selectedNotification.notificationGroup ===
        NotificationGroups.FileUploadNotification ||
      selectedNotification.notificationGroup ===
        NotificationGroups.EsigRequestCompleteNotifications
    ) {
      return (
        <InboxNotificationTable
          loading={
            filesNotificationDetailsDataReady || fileNotificationDetailsLoading
          }
          rowData={notificationTableRowData}
          notificationGroup={
            selectedNotification.notificationGroup as NotificationGroups
          }
        />
      );
    }

    // this will be replaced by checking event type
    switch (selectedNotification.notificationEventType) {
      case 'forms.completed': {
        const { resourceId: formId, refId: responseRefId } =
          selectedNotification;

        return (
          <>
            <FormNotificationDetailsBreadcrumbs
              formId={formId}
              responseRefId={responseRefId}
              responderName={selectedNotification.avatarInfo?.name || ''}
            />
            <FormResponseDetail
              detailOptions={{
                formId,
                responseRefId,
              }}
            />
          </>
        );
      }

      case 'files.new':
      case 'esign.completed':
        return (
          <InboxNotificationTable
            loading={
              filesNotificationDetailsDataReady ||
              fileNotificationDetailsLoading
            }
            rowData={notificationTableRowData}
            notificationGroup={
              selectedNotification.notificationGroup as NotificationGroups
            }
          />
        );

      default:
        return null;
    }
  };

  // this method handle deleting a notification from
  // the notification details page toolbar.
  const handleDeleteNotification = async () => {
    await deleteNotification({
      id: encodeURIComponent(selectedNotification?.id || ''),
    });
    setSelectedNotification(null);
  };

  const { handleExportForm: exportForm } = useExportFormResponse({
    formId: selectedNotification?.resourceId || '',
    responseRefId: selectedNotification?.refId || '',
  });

  // this method handles exporting a notification form
  // TODO: implement this method later
  const handleExportForm = () => {
    exportForm({
      ref: selectedNotification?.refId || '',
    });
  };

  // this method is responsible of calculating the inbox content
  // toolbar actions based on the selected notification type.
  // e.g: delete notification, download file, etc.
  const getNotificationContentToolbarActions = () => {
    const actions = [
      <Button
        htmlId="delete-notification-action-btn"
        variant="contained"
        color="secondary"
        onClick={handleDeleteNotification}
        className={classes.deleteNotificationBtn}
        startIcon={
          <TrashIcon
            style={{
              fontSize: 8,
            }}
          />
        }
      >
        Delete notification
      </Button>,
    ];

    // when it is a file upload notification only show delete action
    if (selectedNotification?.notificationEventType === 'files.new') {
      return actions;
    }

    // for other preview-able notifications show the download action
    // 1. when it is form notification show export form actions
    if (selectedNotification?.notificationEventType === 'forms.completed') {
      actions.unshift(
        <Button
          htmlId="download-notification-action-btn"
          variant="contained"
          color="secondary"
          onClick={handleExportForm}
          startIcon={
            <ExportIcon
              style={{
                fontSize: 8,
              }}
            />
          }
        >
          Export form
        </Button>,
      );
      return actions;
    }

    // TODO: implement other actions for other notification types
    // e.g: download invoice, download contract, etc.

    return actions;
  };

  // when no notification is selected
  // render empty page
  if (!selectedNotification) return <InboxEmptyState />;

  const showTimeline =
    selectedNotificationDetails &&
    selectedNotificationDetails.timeline &&
    selectedNotificationDetails.timeline.length > 0;

  return (
    <div
      className={classes.container}
      data-testid="inbox-selected-notification"
    >
      <ChannelContentToolbar
        title={
          EnableNotificationSidebarItemRedesign
            ? selectedNotification.description
            : selectedNotification.label
        }
        onBackClick={() => {
          history.replace({
            search: '',
          });
        }}
        actions={[]} // no actions for notification content for now
        AdditionalSlotsComponents={getNotificationContentToolbarActions().concat(
          showTimeline
            ? [<RightSidebarToggle key="right-sidebar-toggle" />]
            : [],
        )}
      />
      {notificationDetailsContentRenderer()}
      {showTimeline && (
        <RightSidebar
          sticky={false}
          width={NOTIFICATION_TIMELINE_DRAWER_WIDTH}
          showOnMount
        >
          <NotificationTimeline events={selectedNotificationDetails.timeline} />
        </RightSidebar>
      )}
    </div>
  );
};
